import {
  ReferenceArrayInput, Button, DeleteButton, EditButton, SaveButton, SimpleForm,
  TextField, Toolbar, TopToolbar, useDataProvider, useNotify, useRefresh, useTranslate, useResourceContext,
} from 'react-admin';
import { Datagrid } from '@react-admin/ra-rbac';
import { ListLive } from '@react-admin/ra-realtime';
import { required, useRecordContext } from 'ra-core';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Checkbox, CircularProgress,
} from '@mui/material';
import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { SecurityInventoryCategory, TimeGranularityType } from '@x-guard/xgac-types/xgac';
import { LIST_DEBOUNCE } from '../../../config';
import SearchFields from '../components/fields/Filters';
import { TimeGranularityField } from '../../../components/fields/TimeGranularityField';
import { EditWithSuccess } from '../../../components/baseForms/EditWithSuccess';
import { GrayTextInputWithUpperLabel } from '../components/inputs/GrayTextInputWithUpperLabel';
import { GrayAutocompleteArrayInputWithUpperLabel } from '../components/inputs/GrayAutocompleteArrayInputWithUpperLabel';
import { AssetsInDialogButton } from '../../../components/buttons/assetsInDialogButton';
import { GrayTimeGranularityInputWithUpperLabel } from '../components/inputs/GrayTimeGranularityInputWithUpperLabel';
import { IconSelector } from '../components/inputs/IconSelector';
import { IconField } from '../components/fields/IconField';
import { InventoryCategoryIconName } from '../components/icons/inventoryCategoryIcons';

type InventoryCategoryPreset = Omit<SecurityInventoryCategory, '_id' | 'customer' | 'share' | 'icon' | 'createdAt' | 'updatedAt'>
& { icon: InventoryCategoryIconName };

const categoryPresets: InventoryCategoryPreset[] = [
  {
    name: 'Automated External Defibrillators',
    icon: 'monitor-heart',
    expiryInterval: {
      type: TimeGranularityType.Year,
      value: 1,
    },
    peopleToNotify: [],
    description: 'Emergency response equipment intended to support rapid intervention in incidents involving sudden cardiac arrest.',
  },
  {
    name: 'Fire extinguishers',
    icon: 'fire-extinguisher',
    expiryInterval: {
      type: TimeGranularityType.Month,
      value: 6,
    },
    peopleToNotify: [],
    description: 'Handheld safety devices designed to suppress or extinguish small fires before they spread.',
  },
  {
    name: 'First-aid kits',
    icon: 'medical-services',
    expiryInterval: {
      type: TimeGranularityType.Year,
      value: 1.5,
    },
    peopleToNotify: [],
    description: 'Collections of essential medical supplies used to provide immediate basic treatment for injuries until professional care is available.',
  },
];

const InventoryCategoryCreateDialog = (props: { open: boolean; onClose: () => void }) => {

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const dataProvider = useDataProvider();
  const resource = useResourceContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  const handleClose = () => {

    setSelectedItems([]);
    props.onClose();

  };

  const handleToggleSelect = (name: string) => {

    setSelectedItems((current) => {

      if (current.includes(name)) return current.filter((item) => item !== name);
      return [...current, name];

    });

  };

  const handleAdd = async () => {

    setIsLoading(true);

    try {

      const selected = categoryPresets.filter((row) => selectedItems.includes(row.name));

      if (selected.length === 0) {

        return;

      }

      await Promise.all(
        selected.map((item) => dataProvider.create(resource || 'security-inventory-categories', { data: { ...item } })),
      );

      notify(`${translate('resources.security-inventory-categories.text.title_single')} ${translate('general.text.saved').toLowerCase()}`, { type: 'success' });
      refresh();

      handleClose();

    } catch (error) {

      if (error instanceof Error) {

        notify(error.message, { type: 'error' });

      }

    } finally {

      setIsLoading(false);

    }

  };

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>{translate('resources.security-inventory-categories.text.add_from_presets_title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{translate('resources.security-inventory-categories.text.add_from_presets_subtitle')}</DialogContentText>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>{translate('general.fields.name')}</TableCell>
                <TableCell>{translate('resources.security-inventory-categories.fields.icon')}</TableCell>
                <TableCell>{translate('resources.security-inventory-categories.fields.inspection_period')}</TableCell>
                <TableCell>{translate('general.fields.description')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categoryPresets.map((row) => {

                const isSelected = selectedItems.includes(row.name);

                return (
                  <TableRow key={row.name} onClick={() => handleToggleSelect(row.name)} hover selected={isSelected}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        value={row.name}
                      />
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell><IconField source="icon" iconName={row.icon} /></TableCell>
                    <TableCell>
                      {row.expiryInterval.value} {translate(`resources.report-triggers.text.granularity_types.${row.expiryInterval.type}`).toLowerCase()}
                    </TableCell>
                    <TableCell>{row.description}</TableCell>
                  </TableRow>
                );

              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button label="general.text.close" size="medium" onClick={handleClose} />
        <Button label="general.text.add" size="medium" onClick={handleAdd}
          startIcon={isLoading ? <CircularProgress color='inherit' size={20} /> : undefined} disabled={selectedItems.length === 0 || isLoading} />
      </DialogActions>
    </Dialog>
  );

};

export const InventoryCategoriesList = () => {

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const translate = useTranslate();

  return (
    <>
      <h3>{translate('resources.security-inventory-categories.title')}</h3>
      <p>{translate('resources.security-inventory-categories.text.sub_title')}</p>
      <ListLive filters={SearchFields} debounce={LIST_DEBOUNCE}
        actions={
          <TopToolbar>
            <Button label="general.text.add_from_presets" size="medium" startIcon={<AddIcon/>} onClick={() => setCreateDialogOpen(true)} />
          </TopToolbar>
        }>
        <Datagrid rowClick={false} bulkActionButtons={false}>
          <TextField source="name" label="general.fields.name"/>
          <IconField source="icon" sortable={false} />
          <TimeGranularityField source="expiryInterval" label="resources.security-inventory-categories.fields.inspection_period"/>
          <AssetsInDialogButton label="resources.security-inventory-categories.fields.people_to_notify" resource="security-inventory-categories" />
          <EditButton resource="security-inventory-categories"/>
          <DeleteButton resource="security-inventory-categories"/>
        </Datagrid>
      </ListLive>
      <InventoryCategoryCreateDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}/>
    </>
  );

};

const inventoryCategoryEditTransformer = (data: any) => {

  return {
    ...data,
    peopleToNotify: (data.peopleToNotify ?? []).map((personId: string) => ({
      _id: personId,
      _ref: 'Asset',
    })),
  };

};

const InventoryCategoryEditToolbar = () => {

  const navigate = useNavigate();

  return (
    <Toolbar sx={{ justifyContent: 'space-between', maxWidth: '1000px' }}>
      <Button label='ra.action.back' onClick={() => navigate(-1)} size="medium" sx={{ marginRight: '10px' }} variant="text"/>
      <SaveButton />
    </Toolbar>
  );

};

const InventoryCategoryEditForm = () => {

  const translate = useTranslate();
  const record = useRecordContext();

  return (
    <>
      <h4 style={{ marginBottom: '10px' }}>"{record?.name}" {translate('general.text.settings')}</h4>
      <Typography variant="body1">{translate('resources.security-inventory-categories.text.settings_sub_title')}</Typography>
      <hr/>
      <GrayTextInputWithUpperLabel source="name" validate={required()} label="general.fields.name"/>
      <IconSelector source="icon" label="resources.security-inventory-categories.fields.icon" />
      <ReferenceArrayInput source="peopleToNotify" reference="app-users" label="resources.security-inventory-categories.fields.people_to_notify">
        <GrayAutocompleteArrayInputWithUpperLabel label="resources.security-inventory-categories.fields.people_to_notify"/>
      </ReferenceArrayInput>
      <GrayTimeGranularityInputWithUpperLabel source="expiryInterval" label="resources.security-inventory-categories.fields.inspection_period"/>
      <GrayTextInputWithUpperLabel source="description" label="general.fields.description" multiline rows={4} />
    </>
  );

};

export const InventoryCategoryEdit = () => {

  return (
    <>
      <EditWithSuccess transform={inventoryCategoryEditTransformer} title="resources.security-inventory-categories.title" actions={false}>
        <SimpleForm sx={{ maxWidth: '1000px' }} toolbar={<InventoryCategoryEditToolbar/>}>
          <InventoryCategoryEditForm/>
        </SimpleForm>
      </EditWithSuccess>
    </>
  );

};
